import mixpanel from 'mixpanel-browser';
import { doc, getDoc, collection, query, limit, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { getActiveSubscription } from './subscription';
import { hasComplimentaryExpiredMarker } from './subscriptionState';

const isDev = process.env.REACT_APP_ENVIRONMENT === 'development';

// ⚠️  SUBSCRIPTION STATUS STRINGS ARE DEFINED IN TWO PLACES:
//   1. Here  — UI/react/stack/src/utils/analytics.js  (Mixpanel / React)
//   2. Backend — backend/firebase/functions/mailerliteSync/user-state.js  (Cloud Functions)
//
// CRA prevents cross-environment imports so these cannot share a single file.
// Any change to a status string MUST be mirrored in both files.
//
// Canonical lowercase values: grandfathered | trialing | active_paid | lapsed |
// complimentary_expired | never_subscribed | unknown
// Note: this side collapses lapsed_paid + lapsed_trial → 'lapsed' (single Mixpanel bucket).
// The Cloud Functions side keeps them split for finer-grained MailerLite segmentation.

// Module-level cache so subscription_status is appended to every event.
// 'unknown' for anonymous/unauthenticated users.
const VALID_SUBSCRIPTION_STATUSES = new Set([
  'unknown', 'grandfathered', 'trialing', 'active_paid', 'lapsed', 'complimentary_expired', 'never_subscribed',
]);

let cachedSubscriptionStatus = 'unknown';

export const setSubscriptionStatus = (status) => {
  if (VALID_SUBSCRIPTION_STATUSES.has(status)) {
    cachedSubscriptionStatus = status;
    // Register as a super property so Mixpanel attaches it to ALL events,
    // including auto-captured page views that bypass logAnalyticsEvent.
    mixpanel.register({ subscription_status: status });
  } else if (isDev) {
    console.warn('[analytics] Unknown subscription_status value:', status);
  }
};

/**
 * Before STACK-594 a signed-in member was identify()'d, and mixpanel-browser
 * keeps that identity in localStorage until reset(), so a member who never
 * signed out would go on sending their account id as distinct_id. Call this
 * before mixpanel.init(): init sends the first page view at once, so a reset
 * after it would come too late. When the stored identity names a member, it
 * removes it, with any events and profile updates still queued under it, and
 * init starts a fresh device id.
 */
export const forgetLegacyMixpanelIdentity = (token, storage = typeof window !== 'undefined' ? window.localStorage : null) => {
  try {
    if (!token || !storage) return;
    const key = `mp_${token}_mixpanel`;
    const stored = JSON.parse(storage.getItem(key) || 'null');
    if (!stored || !stored.$user_id) return;
    const queuePrefix = `__mpq_${token}`;
    Object.keys(storage)
      .filter((k) => k === key || k.startsWith(queuePrefix))
      .forEach((k) => storage.removeItem(k));
  } catch (error) {
    if (isDev) console.warn('[analytics] forgetLegacyMixpanelIdentity failed:', error);
  }
};

/**
 * Works out a signed-in member's subscription_status from Firestore and
 * registers it on every event. Mixpanel never learns who the member is: no
 * identify(), no People profile (Ruling, Aravind, 2026-10-16 — STACK-594).
 * Analytics needs only whether someone is a subscriber, and a member is one
 * device to Mixpanel, like any visitor.
 *
 * Status values: 'grandfathered' | 'trialing' | 'active_paid' | 'lapsed' |
 * 'complimentary_expired' | 'never_subscribed'
 */
export const identifyUser = async (uid) => {
  try {
    // Reset immediately so events fired during async lookup aren't tagged with a prior user's status
    setSubscriptionStatus('unknown');

    const [userSnap, activeSub] = await Promise.all([
      getDoc(doc(db, 'users', uid)),
      getActiveSubscription(uid),
    ]);

    // Guard against stale async update (e.g. rapid logout/login): drop a
    // lookup that finished for someone who is no longer the signed-in user.
    if (auth.currentUser?.uid !== uid) return;

    const userData = userSnap.exists() ? userSnap.data() : {};
    const isGrandfathered = userData.isGrandfathered === true;

    const complimentaryExpired = hasComplimentaryExpiredMarker(userData);
    const complimentaryEndedAtIso = toIsoOrNull(userData.grandfatheredEndedAt);

    let subscriptionStatus;

    if (isGrandfathered) {
      subscriptionStatus = 'grandfathered';
    } else if (activeSub) {
      // Normalize trial_end — may be Firestore Timestamp, Date, numeric epoch, or string
      const rawTrialEnd = activeSub.trial_end;
      let trialEnd = null;
      if (rawTrialEnd?.toDate) {
        trialEnd = rawTrialEnd.toDate();
      } else if (rawTrialEnd instanceof Date) {
        trialEnd = rawTrialEnd;
      } else if (typeof rawTrialEnd === 'number') {
        trialEnd = new Date(rawTrialEnd < 1e12 ? rawTrialEnd * 1000 : rawTrialEnd);
      } else if (rawTrialEnd) {
        const d = new Date(rawTrialEnd);
        if (!Number.isNaN(d.getTime())) trialEnd = d;
      }

      subscriptionStatus = (trialEnd && trialEnd > new Date()) ? 'trialing' : 'active_paid';
    } else {
      // Use limit(1) — only need to know if any subscription history exists
      const historySnap = await getDocs(query(collection(db, 'customers', uid, 'subscriptions'), limit(1)));
      if (!historySnap.empty) {
        subscriptionStatus = 'lapsed';
      } else {
        // No Stripe history: distinguish ended-complimentary users from
        // genuinely never-subscribed accounts.
        subscriptionStatus = complimentaryExpired ? 'complimentary_expired' : 'never_subscribed';
      }
    }

    setSubscriptionStatus(subscriptionStatus);
    if (complimentaryExpired) {
      mixpanel.register({
        had_complimentary_access: true,
        ...(complimentaryEndedAtIso && { complimentary_access_ended_at: complimentaryEndedAtIso }),
      });
    }
  } catch (error) {
    if (isDev) console.warn('[analytics] identifyUser failed:', error);
  }
};

// Firestore Timestamp | Date | ISO string → ISO string (or null)
const toIsoOrNull = (value) => {
  try {
    if (!value) return null;
    if (typeof value.toDate === 'function') return value.toDate().toISOString();
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  } catch (_) {
    return null;
  }
};

/**
 * Keep the subscription_status super property in sync with mid-session user
 * document changes (e.g. the complimentary-expiry migration flipping
 * isGrandfathered off), so an open session doesn't stay tagged
 * 'grandfathered'. Called from the AuthContext Firestore listeners.
 *
 * Only handles the transitions those listeners can observe; the full
 * (history-aware) classification still happens in identifyUser at login.
 * Precedence mirrors the canonical resolver: grant → live Stripe → marker.
 */
export const refreshSubscriptionStatusSuperProperty = (userData, stripeStatus) => {
  try {
    if (!userData) return;

    if (userData.isGrandfathered === true) {
      setSubscriptionStatus('grandfathered');
      return;
    }
    if (stripeStatus === 'trialing') {
      setSubscriptionStatus('trialing');
      return;
    }
    if (stripeStatus === 'active') {
      setSubscriptionStatus('active_paid');
      return;
    }
    if (hasComplimentaryExpiredMarker(userData)) {
      const endedAtIso = toIsoOrNull(userData.grandfatheredEndedAt);
      setSubscriptionStatus('complimentary_expired');
      mixpanel.register({
        had_complimentary_access: true,
        ...(endedAtIso && { complimentary_access_ended_at: endedAtIso }),
      });
    }
    // Anything else (lapsed vs never_subscribed) needs the history query —
    // leave the identifyUser-provided value in place.
  } catch (error) {
    if (isDev) console.warn('[analytics] refreshSubscriptionStatusSuperProperty failed:', error);
  }
};

// ─── Google Ads attribution ───────────────────────────────────────────────────
// mixpanel-browser auto-registers UTM params as super properties but not
// Google's auto-tag params and myStack campaign params. When these are present
// in the landing URL we register them immediately so they attach to every
// downstream funnel event in the same session.
// mixpanel.register() persists to Mixpanel's own localStorage so the props
// survive SPA navigation — no extra storage layer needed.
//
// For cross-session attribution (e.g. trial → paid conversion days later),
// gtag automatically stores the gclid in a _gcl_aw cookie (90-day TTL) when
// auto-tagging is enabled in Google Ads. We read that as a fallback so
// Mixpanel attribution stays consistent with Google's own conversion tracking.
//
// Accepts optional overrides for search and cookies to make unit testing
// straightforward without globals mocking.
const GADS_KEYS = ['gclid', 'gad_source', 'gad_campaignid', 'gbraid', 'wbraid', 'mystack_campaign_id', 'mystack_variant_id'];

export const getGoogleAdsAttributionProps = (search = typeof window !== 'undefined' ? window.location.search : '') => {
  const params = new URLSearchParams(search);
  const props = {};
  GADS_KEYS.forEach(key => {
    const val = params.get(key);
    if (val) props[key] = val;
  });
  return props;
};

export const captureGoogleAdsAttribution = ({
  search = typeof window !== 'undefined' ? window.location.search : '',
  cookies = typeof document !== 'undefined' ? document.cookie : '',
} = {}) => {
  if (typeof window === 'undefined') return;

  // 1. Fresh click — URL params take priority (last-touch)
  const props = getGoogleAdsAttributionProps(search);

  // 2. Cross-session fallback — read gclid from Google's _gcl_aw cookie.
  //    gtag sets this automatically when auto-tagging is on; format: GCL.{ts}.{gclid}
  if (!props.gclid) {
    const gclCookie = cookies.split('; ').find(r => r.startsWith('_gcl_aw='));
    if (gclCookie) {
      const parts = gclCookie.split('=')[1].split('.');
      if (parts.length >= 3) props.gclid = parts.slice(2).join('.');
    }
  }

  // Unregister all known gads keys before re-registering so stale values
  // from a prior click (e.g. gbraid from click 1) don't persist into click 2.
  GADS_KEYS.forEach(key => mixpanel.unregister(key));

  if (Object.keys(props).length > 0) mixpanel.register(props);
};
// ─────────────────────────────────────────────────────────────────────────────

// Google Ads conversion actions (account 9562092305). Ruling (Aravind,
// 2026-10-12): a trial start — Stripe checkout completed — is the conversion
// the campaigns count; a sign-up is tracked but not counted. Which actions
// count is set in Google Ads and journaled (ads/google/state/journal.jsonl).
export const GOOGLE_ADS_CONVERSIONS = {
  TRIAL_STARTED: 'AW-16692189167/mun0CPP19podEO-vupc-',
  SIGN_UP: 'AW-16692189167/irE3CPb19podEO-vupc-',
};

/**
 * Fire a Google Ads conversion event. The Ads tag never runs on the site's own
 * pages (public/ads-tag.html says why): the conversion is sent from a hidden
 * frame, and attributes through the _gcl_aw cookie set on the ad landing.
 * @param {string} conversionLabel - The full send_to value, e.g. 'AW-XXXXXXXXX/label'
 * @param {Object} opts - Optional: { value, currency }
 */
export const logGoogleAdsConversion = (conversionLabel, { value = 1.0, currency = 'USD' } = {}) => {
  if (typeof window === 'undefined' || !window.openGoogleAdsFrame) return;
  try {
    const query = new URLSearchParams({ conversion: conversionLabel, value: String(value), currency });
    window.openGoogleAdsFrame(query.toString());
  } catch (_) {}
};

export const logAnalyticsEvent = (eventName, params = {}) => {
  // Get platform from window object, default to 'web'
  const platform = window.appPlatform || 'web';
  // Merge platform, subscription_status into every event
  const eventParams = { ...params, app_platform: platform, subscription_status: cachedSubscriptionStatus };

  // Never through gtag: an event without send_to goes to every tag on the
  // page, Google Ads included, with its parameters — health goals among them.
  // (Measured 2026-10-12: one event reached four Ads remarketing endpoints.)

  // Log to Mixpanel
  try {
    mixpanel.track(eventName, eventParams);
    //console.log(`Mixpanel Event: ${eventName}`, eventParams);
  } catch (error) {
    // console.error('Error logging Mixpanel event:', error);
  }
}; 